import type {
  AuthProviderInfo,
  BackstageIdentityResponse,
  DiscoveryApi,
  OAuthRequestApi,
  ProfileInfo,
} from '@backstage/core-plugin-api';
import {
  OAuth2,
  openLoginPopup,
  type AuthConnector,
  type AuthConnectorCreateSessionOptions,
  type AuthConnectorRefreshSessionOptions,
  type OAuth2Session,
} from '@backstage/core-app-api';
import {
  DEFAULT_RATE_LIMIT_MS,
  type RefreshBlock,
  type RefreshLatch,
} from './refreshLatch';

/**
 * The auth connector of the Authelia API: upstream's `DefaultAuthConnector`
 * (@backstage/core-app-api 1.20.5, lib/AuthConnector/DefaultAuthConnector.esm.js,
 * not exported) plus OAuth2's session transform, with one change — a refresh
 * that fails is not tried again on its own.
 *
 * Why (incident 2026-10-17, Authelia locked for ~1 h by its rate limit on
 * `/api/oidc/token`): upstream's `RefreshingAuthSessionManager.getSession`
 * calls `refreshSession` every time it holds no session, and keeps nothing
 * of a failure. Every `getIdToken({ optional: true })` of the ArgoCD cards,
 * every `getCredentials()` of the fetch API, every page load's sign-in check
 * then asks the backend to refresh, and the backend Authelia — whose refresh
 * token had expired, so every call failed and none stopped the next.
 *
 * Here a refresh the auth backend refused (its 401) sets the
 * {@link RefreshLatch} (localStorage, see `blockFor` for what counts): until
 * the user clicks to sign in, `refreshSession` throws at once, with no
 * request. A session lost while the app was shown (Authelia refused its
 * refresh) reloads the page once, onto the sign-in page — which, latched,
 * asks nothing. A 503 of the backend's guard (Authelia rate-limits) holds
 * refreshes until its `Retry-After`, without reloading.
 */

/** The answer of `/refresh` and of the popup, as the auth backend sends it. */
interface AuthResponse {
  profile: ProfileInfo;
  providerInfo: {
    idToken: string;
    accessToken: string;
    scope: string;
    expiresInSeconds?: number;
  };
  backstageIdentity?: {
    token: string;
    identity: BackstageIdentityResponse['identity'];
    expiresInSeconds?: number;
  };
}

/** Thrown, without any request, while refreshes are held back. */
export class RefreshBlockedError extends Error {
  constructor(readonly block: RefreshBlock) {
    super(`Rafraîchissement de session suspendu (${block.reason})`);
    this.name = 'RefreshBlockedError';
  }
}

export interface WaadooAuthConnectorOptions {
  discoveryApi: DiscoveryApi;
  oauthRequestApi: OAuthRequestApi;
  environment: string;
  provider: AuthProviderInfo & { id: string };
  /** `enableExperimentalRedirectFlow` of app-config. */
  redirectFlow: boolean;
  latch: RefreshLatch;
  /** Called once a session of this page is lost: reloads, by default. */
  onSessionLost?: () => void;
  now?: () => number;
}

/** The marker of the backend guard's 503 (`authRefreshGuard.ts`). */
const GUARD_MARKER = 'cooldown';

export class WaadooAuthConnector implements AuthConnector<OAuth2Session> {
  private readonly authRequester: (
    scopes: Set<string>,
  ) => Promise<OAuth2Session>;
  private readonly now: () => number;
  /** Whether this page held a session: a later failure means it was lost. */
  private hadSession = false;
  private lostReported = false;

  constructor(private readonly options: WaadooAuthConnectorOptions) {
    this.now = options.now ?? Date.now;
    this.authRequester = options.oauthRequestApi.createAuthRequester({
      provider: options.provider,
      // Runs when the user clicks in the login dialog, never by itself.
      onAuthRequest: async scopes => {
        options.latch.clear();
        return this.options.redirectFlow
          ? this.executeRedirect(scopes)
          : this.showPopup(scopes);
      },
    });
  }

  async createSession(
    options: AuthConnectorCreateSessionOptions,
  ): Promise<OAuth2Session> {
    if (options.instantPopup) {
      // The sign-in page's button: the user's explicit request.
      this.options.latch.clear();
      if (this.options.redirectFlow) {
        return this.executeRedirect(options.scopes);
      }
      return this.showPopup(options.scopes);
    }
    // Queued for the login dialog; `onAuthRequest` above runs on a click.
    return this.authRequester(options.scopes);
  }

  async refreshSession(
    options?: AuthConnectorRefreshSessionOptions,
  ): Promise<OAuth2Session> {
    const held = this.options.latch.read();
    if (held) {
      throw new RefreshBlockedError(held);
    }
    const res = await fetch(
      await this.buildUrl('/refresh', {
        optional: true,
        ...(options && { scope: [...options.scopes].join(' ') }),
      }),
      {
        headers: { 'x-requested-with': 'XMLHttpRequest' },
        credentials: 'include',
      },
    ).catch(error => {
      // The backend was not reached: Authelia was not either. Not latched.
      throw new Error(`Auth refresh request failed, ${error}`);
    });
    if (!res.ok) {
      const block = await this.blockFor(res);
      if (block) {
        this.options.latch.block(block);
        this.reportLost(block);
      }
      const error = new Error(
        `Auth refresh request failed, ${res.statusText}`,
      ) as Error & { status?: number };
      error.status = res.status;
      throw error;
    }
    const authInfo = (await res.json()) as AuthResponse & {
      error?: { name?: string; message: string };
    };
    if (authInfo.error) {
      const block: RefreshBlock = { reason: 'expired' };
      this.options.latch.block(block);
      this.reportLost(block);
      const error = new Error(authInfo.error.message);
      if (authInfo.error.name) {
        error.name = authInfo.error.name;
      }
      throw error;
    }
    this.hadSession = true;
    return this.sessionTransform(authInfo);
  }

  async removeSession(): Promise<void> {
    // Before the request: a page reload from here on asks nothing.
    this.options.latch.block({ reason: 'signedOut' });
    this.hadSession = false;
    const res = await fetch(await this.buildUrl('/logout'), {
      method: 'POST',
      headers: { 'x-requested-with': 'XMLHttpRequest' },
      credentials: 'include',
    }).catch(error => {
      throw new Error(`Logout request failed, ${error}`);
    });
    if (!res.ok) {
      const error = new Error(
        `Logout request failed, ${res.statusText}`,
      ) as Error & { status?: number };
      error.status = res.status;
      throw error;
    }
    const contentType = res.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      const body = (await res.json().catch(() => undefined)) as
        | { logoutUrl?: string }
        | undefined;
      if (body?.logoutUrl) {
        let url: URL | undefined;
        try {
          url = new URL(body.logoutUrl);
        } catch {
          // Unparsable: ignored, as upstream does.
          url = undefined;
        }
        if (
          url &&
          (url.protocol === 'https:' || url.hostname === 'localhost')
        ) {
          window.location.href = body.logoutUrl;
          await new Promise(() => {});
        }
      }
    }
  }

  /**
   * What a failed `/refresh` answer means for the next ones — only for the
   * answers that say something about the session: a 401 of the auth
   * backend's refresh route (`AuthenticationError` « Refresh failed… »,
   * plugin-auth-node `createOAuthRouteHandlers`), or the 503 of the
   * backend's guard, recognised by its marker
   * (`packages/backend/src/extensions/authRefreshGuard.ts`). Anything else —
   * a 500, a 502/504 or a 503 of the ingress, a 429 of the backend's own
   * limiter — holds nothing back: it is not an answer of Authelia.
   */
  private async blockFor(res: Response): Promise<RefreshBlock | undefined> {
    const body = (await res.json().catch(() => undefined)) as
      | {
          error?: { name?: unknown; message?: unknown };
          waadooAuthGuard?: unknown;
        }
      | undefined;
    if (res.status === 503 && body?.waadooAuthGuard === GUARD_MARKER) {
      const seconds = Number(res.headers.get('retry-after'));
      const wait =
        Number.isFinite(seconds) && seconds > 0
          ? seconds * 1000
          : DEFAULT_RATE_LIMIT_MS;
      return { reason: 'rateLimited', until: this.now() + wait };
    }
    const message = body?.error?.message;
    if (
      res.status !== 401 ||
      body?.error?.name !== 'AuthenticationError' ||
      typeof message !== 'string' ||
      !message.startsWith('Refresh failed')
    ) {
      return undefined;
    }
    // No refresh cookie at all: Authelia was not asked.
    if (message.includes('Missing session cookie')) {
      return { reason: 'noSession' };
    }
    return { reason: 'expired' };
  }

  /** A session this page held is gone: once, back to the sign-in page. */
  private reportLost(block: RefreshBlock): void {
    if (!this.hadSession || block.reason === 'rateLimited') {
      return;
    }
    this.hadSession = false;
    if (this.lostReported) {
      return;
    }
    this.lostReported = true;
    (this.options.onSessionLost ?? (() => window.location.reload()))();
  }

  private async showPopup(scopes: Set<string>): Promise<OAuth2Session> {
    const popupUrl = await this.buildUrl('/start', {
      scope: [...scopes].join(' '),
      origin: window.location.origin,
      flow: 'popup',
    });
    const payload = (await openLoginPopup({
      url: popupUrl,
      name: `${this.options.provider.title} Login`,
      width: 450,
      height: 730,
    })) as AuthResponse;
    this.hadSession = true;
    return this.sessionTransform(payload);
  }

  private async executeRedirect(scopes: Set<string>): Promise<OAuth2Session> {
    window.location.href = await this.buildUrl('/start', {
      scope: [...scopes].join(' '),
      origin: window.location.origin,
      redirectUrl: window.location.href,
      flow: 'redirect',
    });
    return new Promise(() => {});
  }

  private async buildUrl(
    path: string,
    query: Record<string, string | boolean | undefined> = {},
  ): Promise<string> {
    const baseUrl = await this.options.discoveryApi.getBaseUrl('auth');
    const params = Object.entries({ ...query, env: this.options.environment })
      .map(([key, value]) => {
        if (typeof value === 'string') {
          return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        }
        return value ? encodeURIComponent(key) : undefined;
      })
      .filter(Boolean)
      .join('&');
    return `${baseUrl}/${this.options.provider.id}${path}${
      params ? `?${params}` : ''
    }`;
  }

  /** OAuth2's own transform (`OAuth2.createAuthConnector`). */
  private sessionTransform(res: AuthResponse): OAuth2Session {
    const now = this.now();
    const session: OAuth2Session = {
      profile: res.profile,
      providerInfo: {
        idToken: res.providerInfo.idToken,
        accessToken: res.providerInfo.accessToken,
        scopes: OAuth2.normalizeScopes(res.providerInfo.scope),
        expiresAt: res.providerInfo.expiresInSeconds
          ? new Date(now + res.providerInfo.expiresInSeconds * 1000)
          : undefined,
      },
    };
    if (res.backstageIdentity) {
      session.backstageIdentity = {
        token: res.backstageIdentity.token,
        identity: res.backstageIdentity.identity,
        expiresAt: res.backstageIdentity.expiresInSeconds
          ? new Date(now + res.backstageIdentity.expiresInSeconds * 1000)
          : undefined,
      };
    }
    return session;
  }
}
